import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PDFDocument } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';

// Set up PDF.js worker - use local copy for reliability
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

/**
 * Interactive PDF Form Filler
 *
 * Extracts existing form fields from PDFs and presents them as a form.
 * Users fill in the fields and download a completed PDF.
 */

// Form categories
const FORM_CATEGORIES = {
  General: { title: 'General Forms' },
  Multisite: { title: 'Multisite Forms' },
  Miscellaneous: { title: 'Miscellaneous' },
  Mortgages_UCCs: { title: 'Mortgages / UCCs' },
  Transfer_Tax: { title: 'Transfer Tax' },
  Retainer_Deeds: { title: 'Retainer & Deeds' },
  Contracts_of_Sale: { title: 'Contracts of Sale' },
};

export default function FormFiller() {
  // Form selection
  const [formsManifest, setFormsManifest] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});

  // PDF state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pdfBytes, setPdfBytes] = useState(null);

  // Form fields extracted from PDF
  const [formFields, setFormFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const canvasRef = useRef(null);
  const [pdfJsDoc, setPdfJsDoc] = useState(null);
  const [canvasScale, setCanvasScale] = useState(1);

  // Load forms manifest
  useEffect(() => {
    fetch('/forms/forms_manifest.json')
      .then(res => res.json())
      .then(data => setFormsManifest(data))
      .catch(err => console.error('Failed to load forms manifest:', err));
  }, []);

  // Load PDF when form is selected
  useEffect(() => {
    if (!selectedForm || !selectedCategory) {
      setPdfJsDoc(null);
      setFormFields([]);
      setFieldValues({});
      return;
    }

    const loadPdf = async () => {
      setIsLoading(true);
      setMessage(null);
      setFormFields([]);
      setFieldValues({});
      setCurrentPage(1);

      try {
        const pdfPath = `/forms/${selectedCategory}/${selectedForm.filename}`;

        // Load with pdf-lib for editing
        const response = await fetch(pdfPath);
        const bytes = await response.arrayBuffer();
        setPdfBytes(bytes);

        const doc = await PDFDocument.load(bytes, { ignoreEncryption: true });
        setTotalPages(doc.getPageCount());

        // Extract form fields
        try {
          const form = doc.getForm();
          const fields = form.getFields();

          const extractedFields = fields.map(field => {
            const name = field.getName();
            const type = field.constructor.name;

            return {
              name,
              type,
              fullType: type,
            };
          });

          setFormFields(extractedFields);

          // Load saved values from localStorage
          const savedKey = `formValues_${selectedCategory}_${selectedForm.filename}`;
          const saved = localStorage.getItem(savedKey);
          if (saved) {
            try {
              setFieldValues(JSON.parse(saved));
            } catch (e) {
              console.error('Failed to load saved values:', e);
            }
          } else {
            // Initialize empty values
            const initialValues = {};
            extractedFields.forEach(field => {
              initialValues[field.name] = '';
            });
            setFieldValues(initialValues);
          }

          if (extractedFields.length === 0) {
            setMessage({
              type: 'info',
              text: 'This PDF does not contain fillable form fields.'
            });
          }
        } catch (err) {
          console.error('Error extracting form fields:', err);
          setMessage({
            type: 'error',
            text: 'Could not extract form fields from this PDF.'
          });
        }

        // Load with PDF.js for rendering
        const pdfJsDocument = await pdfjsLib.getDocument({ data: bytes.slice(0) }).promise;
        setPdfJsDoc(pdfJsDocument);

      } catch (err) {
        setMessage({ type: 'error', text: `Failed to load PDF: ${err.message}` });
      } finally {
        setIsLoading(false);
      }
    };

    loadPdf();
  }, [selectedForm, selectedCategory]);

  // Render PDF page to canvas using PDF.js
  useEffect(() => {
    if (!pdfJsDoc || !canvasRef.current) return;

    const renderPage = async () => {
      const page = await pdfJsDoc.getPage(currentPage);
      const viewport = page.getViewport({ scale: 1.5 }); // Render at 1.5x for clarity

      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({
        canvasContext: context,
        viewport: viewport,
      }).promise;

      // Calculate scale factor for CSS-scaled canvas
      const displayWidth = canvas.offsetWidth;
      const actualWidth = canvas.width;
      const scale = displayWidth / actualWidth;
      setCanvasScale(scale);

      // Get annotations (form fields) from the page
      const annotations = await page.getAnnotations();

      // Map annotations to form fields with their positions
      const fieldsWithPositions = annotations
        .filter(ann => ann.fieldName) // Only get actual form fields
        .map(ann => {
          // PDF rectangles in original PDF coords: [x1, y1, x2, y2] where y1 is bottom, y2 is top
          const rect = viewport.convertToViewportRectangle(ann.rect);
          // After conversion: [left, top, right, bottom] in canvas coordinates
          // The viewport conversion handles the Y-axis flip from PDF to canvas
          const left = Math.min(rect[0], rect[2]);
          const top = Math.min(rect[1], rect[3]);
          const right = Math.max(rect[0], rect[2]);
          const bottom = Math.max(rect[1], rect[3]);

          const x = left;
          const y = top;
          const width = right - left;
          const height = bottom - top;

          return {
            annotationName: ann.fieldName, // Keep annotation name separate
            fieldType: ann.fieldType,
            x,
            y,
            width,
            height,
            page: currentPage,
          };
        });

      // Update form fields with position data
      setFormFields(prev => {
        const updatedFields = prev.map(field => {
          // Match by exact name or by annotation name containing the field name
          const positionData = fieldsWithPositions.find(f =>
            f.annotationName === field.name ||
            f.annotationName.includes(field.name) ||
            field.name.includes(f.annotationName)
          );
          if (positionData) {
            // Merge position data but preserve the original pdf-lib field name
            const { annotationName, ...positionProps } = positionData;
            return { ...field, ...positionProps };
          }
          return field;
        });
        return updatedFields;
      });
    };

    renderPage();
  }, [pdfJsDoc, currentPage]);

  // Save field values to localStorage when they change
  useEffect(() => {
    if (selectedForm && selectedCategory && Object.keys(fieldValues).length > 0) {
      const savedKey = `formValues_${selectedCategory}_${selectedForm.filename}`;
      localStorage.setItem(savedKey, JSON.stringify(fieldValues));
    }
  }, [fieldValues, selectedForm, selectedCategory]);

  // Update a field value
  const updateFieldValue = useCallback((fieldName, value) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldName]: value
    }));
  }, []);

  // Clear all field values
  const clearAllFields = useCallback(() => {
    if (window.confirm('Clear all form fields?')) {
      const emptyValues = {};
      formFields.forEach(field => {
        emptyValues[field.name] = '';
      });
      setFieldValues(emptyValues);
      if (selectedForm && selectedCategory) {
        const savedKey = `formValues_${selectedCategory}_${selectedForm.filename}`;
        localStorage.removeItem(savedKey);
      }
    }
  }, [selectedForm, selectedCategory, formFields]);

  // Generate and download the filled PDF
  const downloadPdf = async () => {
    if (!pdfBytes || !selectedForm) return;

    setIsLoading(true);
    setMessage(null);

    try {
      // Load a fresh copy of the PDF
      const doc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
      const form = doc.getForm();

      // Debug: log available fields in the PDF
      const pdfFields = form.getFields();
      const pdfFieldNames = pdfFields.map(f => f.getName());
      console.log('PDF field names:', pdfFieldNames);
      console.log('Field values to fill:', fieldValues);

      let filledCount = 0;

      // Fill in the form fields
      for (const [fieldName, value] of Object.entries(fieldValues)) {
        // Convert value to string for consistent checking
        const strValue = value?.toString() ?? '';
        if (!strValue || strValue.trim() === '') continue;

        console.log(`Processing field "${fieldName}" with value "${strValue}"`);

        try {
          const field = form.getField(fieldName);
          const fieldType = field.constructor.name;
          console.log(`Field "${fieldName}" found, type: ${fieldType}`);

          if (fieldType === 'PDFTextField') {
            console.log(`Calling setText("${value}") on "${fieldName}"...`);
            field.setText(value);
            console.log(`Successfully filled "${fieldName}"`);
            filledCount++;
          } else if (fieldType === 'PDFCheckBox') {
            // Handle checkbox - check if value is truthy
            if (value === 'true' || value === true || value === 'yes' || value === 'Yes') {
              field.check();
              filledCount++;
            } else {
              field.uncheck();
            }
          } else if (fieldType === 'PDFRadioGroup') {
            // Handle radio button
            try {
              field.select(value);
              filledCount++;
            } catch (e) {
              console.warn(`Could not select radio option "${value}" for field "${fieldName}"`);
            }
          } else if (fieldType === 'PDFDropdown') {
            // Handle dropdown
            try {
              field.select(value);
              filledCount++;
            } catch (e) {
              console.warn(`Could not select dropdown option "${value}" for field "${fieldName}"`);
            }
          } else {
            // Try setText for any other field type that might support it
            console.log(`Attempting setText on unknown field type: ${fieldType}`);
            try {
              if (typeof field.setText === 'function') {
                field.setText(value);
                filledCount++;
              } else {
                console.warn(`Field "${fieldName}" of type ${fieldType} does not support setText`);
              }
            } catch (e) {
              console.warn(`Could not set text on field "${fieldName}":`, e.message);
            }
          }
        } catch (err) {
          console.warn(`Could not fill field "${fieldName}":`, err.message);
          // Try to find a matching field by partial name
          const matchingField = pdfFields.find(f =>
            f.getName().includes(fieldName) || fieldName.includes(f.getName())
          );
          if (matchingField) {
            console.log(`Found potential match: "${matchingField.getName()}" for "${fieldName}"`);
            try {
              const fieldType = matchingField.constructor.name;
              if (fieldType === 'PDFTextField') {
                matchingField.setText(value);
                filledCount++;
              }
            } catch (e) {
              console.warn(`Could not fill matched field either:`, e.message);
            }
          }
        }
      }

      console.log(`Total fields filled: ${filledCount}`);

      // Flatten the form to make fields non-editable
      // form.flatten();

      // Save and download
      const filledPdfBytes = await doc.save();
      const blob = new Blob([filledPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedForm.name.replace(/\s+/g, '_')}_filled.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      const nonEmptyCount = Object.values(fieldValues).filter(v => v && v.toString().trim() !== '').length;
      setMessage({
        type: filledCount > 0 ? 'success' : 'warning',
        text: filledCount > 0
          ? `PDF downloaded with ${filledCount} field${filledCount !== 1 ? 's' : ''} filled!`
          : `PDF downloaded but 0 fields were filled. You entered ${nonEmptyCount} value(s). Check browser console for details.`
      });
    } catch (err) {
      console.error('Error generating PDF:', err);
      setMessage({ type: 'error', text: `Failed to generate PDF: ${err.message}` });
    } finally {
      setIsLoading(false);
    }
  };

  // Filter forms based on search
  const getFilteredForms = () => {
    if (!formsManifest) return {};
    if (!searchQuery.trim()) return formsManifest;

    const query = searchQuery.toLowerCase();
    const filtered = {};

    Object.entries(formsManifest).forEach(([category, forms]) => {
      const matchingForms = forms.filter(form =>
        form.name.toLowerCase().includes(query) ||
        category.toLowerCase().includes(query)
      );
      if (matchingForms.length > 0) {
        filtered[category] = matchingForms;
      }
    });

    return filtered;
  };

  const filteredForms = getFilteredForms();

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .form-button:hover { background: var(--surface) !important; color: var(--ink) !important; }
        .form-input:focus { outline: 2px solid var(--gold); outline-offset: 1px; }
        .expand-button:hover { background: var(--surface) !important; }
      `}</style>

      {isLoading && (
        <div style={styles.loadingOverlay}>
          <div style={styles.loadingContent}>
            <div style={styles.spinner}></div>
            <p style={{ color: 'var(--muted)' }}>Processing PDF...</p>
          </div>
        </div>
      )}

      <div style={styles.inner}>
        <header style={styles.header}>
          <h1 style={styles.title}>Interactive Form Filler</h1>
          <p style={styles.subtitle}>
            Select a form, then click anywhere on the PDF to add text. Your entries are saved automatically.
          </p>
        </header>

        <div style={styles.grid}>
          {/* Sidebar - Form Selection */}
          <aside style={styles.sidebar}>
            <h2 style={styles.sidebarTitle}>Select a Form</h2>

            <input
              type="text"
              placeholder="Search forms..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
            />

            {formsManifest ? (
              Object.entries(filteredForms).map(([category, forms]) => {
                const isExpanded = expandedCategories[category];
                const displayedForms = isExpanded ? forms : forms.slice(0, 10);
                const hasMore = forms.length > 10;

                return (
                  <div key={category} style={styles.categoryGroup}>
                    <h3 style={styles.categoryTitle}>
                      {FORM_CATEGORIES[category]?.title || category.replace(/_/g, ' ')}
                    </h3>
                    <div style={styles.formList}>
                      {displayedForms.map((form, index) => (
                        <button
                          key={index}
                          className="form-button"
                          onClick={() => {
                            setSelectedCategory(category);
                            setSelectedForm(form);
                          }}
                          style={{
                            ...styles.formButton,
                            ...(selectedForm?.filename === form.filename ? styles.formButtonActive : {}),
                          }}
                        >
                          {form.name}
                        </button>
                      ))}
                      {hasMore && (
                        <button
                          className="expand-button"
                          onClick={() => {
                            setExpandedCategories(prev => ({
                              ...prev,
                              [category]: !prev[category]
                            }));
                          }}
                          style={styles.expandButton}
                        >
                          {isExpanded ? (
                            <>
                              <span>Show less</span>
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: 'rotate(180deg)' }}>
                                <path d="M2 4l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </>
                          ) : (
                            <>
                              <span>Show {forms.length - 10} more</span>
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M2 4l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Loading forms...</p>
            )}
          </aside>

          {/* Main Content - Form and PDF Preview */}
          <main style={styles.mainContent}>
            {selectedForm ? (
              <>
                {/* Toolbar */}
                <div style={styles.toolbar}>
                  <div style={styles.toolbarLeft}>
                    <h2 style={styles.formTitle}>{selectedForm.name}</h2>
                    {formFields.length > 0 && (
                      <span style={styles.fieldCount}>
                        {formFields.length} field{formFields.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <div style={styles.toolbarRight}>
                    <button onClick={clearAllFields} style={styles.toolBtn}>
                      Clear All
                    </button>
                    <button
                      onClick={downloadPdf}
                      style={{ ...styles.toolBtn, ...styles.toolBtnPrimary }}
                      disabled={formFields.length === 0}
                    >
                      Download PDF
                    </button>
                  </div>
                </div>

                {/* Message */}
                {message && (
                  <div style={
                    message.type === 'success' ? styles.successMessage :
                    message.type === 'error' ? styles.errorMessage :
                    message.type === 'warning' ? styles.warningMessage :
                    styles.infoMessage
                  }>
                    {message.text}
                  </div>
                )}

                {/* Page Navigation */}
                {totalPages > 1 && (
                  <div style={styles.pageNav}>
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage <= 1}
                      style={styles.pageBtn}
                    >
                      &larr; Previous
                    </button>
                    <span style={styles.pageInfo}>
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage >= totalPages}
                      style={styles.pageBtn}
                    >
                      Next &rarr;
                    </button>
                  </div>
                )}

                {/* PDF with Overlay Form Fields */}
                <div style={styles.pdfContainer}>
                  {formFields.length > 0 ? (
                    <div style={styles.pdfWrapper}>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <canvas
                          ref={canvasRef}
                          style={{
                            display: 'block',
                            maxWidth: '100%',
                            height: 'auto',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          }}
                        />
                        {/* Overlay form fields on the PDF */}
                        {formFields
                          .filter(field => field.page === currentPage && field.x !== undefined)
                          .map((field) => {
                            // Don't scale - positions are already in canvas pixels
                            // The CSS maxWidth on canvas scales everything proportionally
                            const scaledX = field.x * canvasScale;
                            const scaledY = field.y * canvasScale;
                            const scaledWidth = field.width * canvasScale;
                            const scaledHeight = field.height * canvasScale;

                            return (
                            <div
                              key={field.name}
                              style={{
                                position: 'absolute',
                                left: `${scaledX}px`,
                                top: `${scaledY}px`,
                                width: `${scaledWidth}px`,
                                height: `${scaledHeight}px`,
                                zIndex: 10,
                              }}
                            >
                              {(field.type === 'PDFTextField' || field.fieldType === 'Tx') && (
                                <input
                                  type="text"
                                  className="form-input"
                                  value={fieldValues[field.name] || ''}
                                  onChange={(e) => updateFieldValue(field.name, e.target.value)}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    padding: '0',
                                    margin: '0',
                                    border: '2px solid rgba(100, 150, 255, 0.6)',
                                    background: 'rgba(173, 216, 230, 0.3)',
                                    fontSize: `${Math.max(8, field.height * canvasScale * 0.5)}px`,
                                    fontFamily: 'Arial, sans-serif',
                                    boxSizing: 'border-box',
                                    color: '#000',
                                    verticalAlign: 'top',
                                  }}
                                  title={field.name}
                                  placeholder=""
                                />
                              )}
                              {(field.type === 'PDFCheckBox' || field.fieldType === 'Btn') && (
                                <input
                                  type="checkbox"
                                  checked={fieldValues[field.name] === 'true' || fieldValues[field.name] === true}
                                  onChange={(e) => updateFieldValue(field.name, e.target.checked.toString())}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    cursor: 'pointer',
                                  }}
                                  title={field.name}
                                />
                              )}
                            </div>
                            );
                          })}
                      </div>
                    </div>
                  ) : (
                    <div style={styles.noFields}>
                      <p style={styles.noFieldsText}>
                        This PDF does not contain fillable form fields.
                      </p>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div style={styles.emptyState}>
                <div style={styles.emptyStateIcon}>
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14,2 14,8 20,8" />
                  </svg>
                </div>
                <h3 style={styles.emptyStateTitle}>Select a Form</h3>
                <p style={styles.emptyStateText}>
                  Choose a form from the sidebar to view the PDF and add text fields.
                </p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: 'var(--surface)',
    padding: 'clamp(1rem, 2vw, 2rem) 0',
  },
  inner: {
    width: '100%',
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '0 clamp(1rem, 2vw, 2rem)',
  },
  header: {
    marginBottom: '1.5rem',
  },
  title: {
    fontFamily: 'var(--font-serif)',
    fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
    color: 'var(--ink)',
    marginBottom: '0.5rem',
  },
  subtitle: {
    fontSize: '1rem',
    color: 'var(--muted)',
    lineHeight: 1.6,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '280px 1fr',
    gap: '1.5rem',
    alignItems: 'start',
  },
  sidebar: {
    background: 'var(--white)',
    border: '1px solid var(--hairline)',
    padding: '1.25rem',
    position: 'sticky',
    top: 'calc(var(--nav-height) + 1rem)',
    maxHeight: 'calc(100vh - var(--nav-height) - 2rem)',
    overflowY: 'auto',
  },
  sidebarTitle: {
    fontFamily: 'var(--font-serif)',
    fontSize: '1.125rem',
    marginBottom: '1rem',
    color: 'var(--ink)',
  },
  searchInput: {
    width: '100%',
    padding: '0.625rem 0.75rem',
    border: '1px solid var(--hairline-strong)',
    background: 'var(--surface)',
    fontFamily: 'inherit',
    fontSize: '0.875rem',
    marginBottom: '1rem',
  },
  categoryGroup: {
    marginBottom: '1.25rem',
  },
  categoryTitle: {
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: 'var(--subtle)',
    marginBottom: '0.5rem',
    fontWeight: 600,
  },
  formList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125rem',
  },
  formButton: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '0.5rem 0.75rem',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    color: 'var(--muted)',
    transition: 'all 0.15s ease',
    borderLeft: '2px solid transparent',
  },
  formButtonActive: {
    background: 'var(--surface)',
    color: 'var(--ink)',
    borderLeftColor: 'var(--gold)',
  },
  expandButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    width: '100%',
    padding: '0.5rem 0.75rem',
    background: 'transparent',
    border: 'none',
    borderTop: '1px solid var(--hairline)',
    cursor: 'pointer',
    fontSize: '0.75rem',
    color: 'var(--gold)',
    fontWeight: '500',
    transition: 'all 0.15s ease',
    marginTop: '0.25rem',
  },
  mainContent: {
    flex: 1,
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '1rem',
    background: 'var(--white)',
    border: '1px solid var(--hairline)',
    borderBottom: 'none',
    padding: '1rem 1.25rem',
  },
  toolbarLeft: {
    flex: 1,
    minWidth: '200px',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  toolbarRight: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  formTitle: {
    fontFamily: 'var(--font-serif)',
    fontSize: '1.25rem',
    color: 'var(--ink)',
    margin: 0,
  },
  fieldCount: {
    fontSize: '0.8125rem',
    color: 'var(--muted)',
    background: 'var(--surface)',
    padding: '0.25rem 0.75rem',
    borderRadius: '12px',
  },
  toolBtn: {
    padding: '0.5rem 1rem',
    border: '1px solid var(--hairline-strong)',
    background: 'var(--surface)',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
  },
  toolBtnPrimary: {
    background: 'var(--ink)',
    color: 'var(--white)',
    borderColor: 'var(--ink)',
  },
  pageNav: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1rem',
    background: 'var(--white)',
    border: '1px solid var(--hairline)',
    borderTop: 'none',
    padding: '0.75rem 1rem',
  },
  pageBtn: {
    padding: '0.5rem 1rem',
    border: '1px solid var(--hairline-strong)',
    background: 'var(--surface)',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
  },
  pageInfo: {
    fontSize: '0.875rem',
    color: 'var(--muted)',
  },
  pdfContainer: {
    background: 'var(--surface)',
    border: '1px solid var(--hairline)',
    borderTop: 'none',
    minHeight: '600px',
    display: 'flex',
    flexDirection: 'column',
  },
  pdfWrapper: {
    padding: '2rem',
    overflowY: 'auto',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    flex: 1,
  },
  noFields: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4rem 2rem',
    flex: 1,
  },
  noFieldsText: {
    color: 'var(--muted)',
    textAlign: 'center',
    lineHeight: 1.6,
  },
  successMessage: {
    background: '#e8f5e9',
    border: '1px solid #a5d6a7',
    borderBottom: 'none',
    padding: '0.75rem 1rem',
    color: '#2e7d32',
    fontSize: '0.875rem',
  },
  errorMessage: {
    background: '#ffebee',
    border: '1px solid #ef9a9a',
    borderBottom: 'none',
    padding: '0.75rem 1rem',
    color: '#c62828',
    fontSize: '0.875rem',
  },
  warningMessage: {
    background: '#fff3e0',
    border: '1px solid #ffcc80',
    borderBottom: 'none',
    padding: '0.75rem 1rem',
    color: '#e65100',
    fontSize: '0.875rem',
  },
  infoMessage: {
    background: '#e3f2fd',
    border: '1px solid #90caf9',
    borderBottom: 'none',
    padding: '0.75rem 1rem',
    color: '#1565c0',
    fontSize: '0.875rem',
  },
  emptyState: {
    textAlign: 'center',
    padding: '4rem 2rem',
    background: 'var(--white)',
    border: '1px solid var(--hairline)',
  },
  emptyStateIcon: {
    width: '64px',
    height: '64px',
    margin: '0 auto 1.5rem',
    color: 'var(--subtle)',
  },
  emptyStateTitle: {
    fontFamily: 'var(--font-serif)',
    fontSize: '1.5rem',
    marginBottom: '0.75rem',
    color: 'var(--ink)',
  },
  emptyStateText: {
    fontSize: '1rem',
    color: 'var(--muted)',
    maxWidth: '400px',
    margin: '0 auto',
  },
  loadingOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(255, 255, 255, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  loadingContent: {
    textAlign: 'center',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid var(--hairline)',
    borderTopColor: 'var(--gold)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 1rem',
  },
};
